import React from 'react';
import { ILandingPageBuilderForm } from '../../../../types';
import videoPlayerImage from '../../../../assets/videoPlayer.png';
import { Flex, Gap } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';
import { MdChat } from 'react-icons/md';
import { IoMdVideocam } from 'react-icons/io';
import {
  AdditionalText,
  AdditionalTextContainer,
  BodyContainer,
  CtaButton,
  CTAButtonsContainer,
  GradientOverlay,
  ReactionsAndCommentsContainer,
  scaleFontSizesInHtml,
  StyledBackgroundImage,
  VideoPlayerImage,
  VideoPlayerThumbnail,
} from '../../styles';
import { REACTIONS, VIDEO_PLAYER_THUMBNAIL_LIST } from '../../constants';
import { VehicleInformation } from './VehicleInformation';
import { MapInformation } from './MapInformation';
import { useTheme } from 'styled-components/macro';
import parse from 'html-react-parser';

interface IBodyProps {
  values: ILandingPageBuilderForm;
  isMobile: boolean;
  showSalesPreviewContent: boolean;
}

export const Body = ({
  values,
  isMobile,
  showSalesPreviewContent,
}: IBodyProps) => {
  const ctaButtonCount = 3;
  const {
    bodyBackgroundColor,
    bodyBackgroundImage,
    buttonFontCase,
    buttonFontWeight,
    buttonFontFamily,
    buttonFontSize,
    buttonCornerRadius,
    buttonPaddingY,
    buttonPaddingX,
    buttonColor,
    buttonBorderColor,
    buttonBorderHoverColor,
    buttonTextColor,
    buttonTextHoverColor,
    showButtonBorder,
    buttonHoverColor,
    additionalText,
    fontFamily,
    showGradient,
    gradient,
    additionalTextBackgroundColor,
    additionalTextColor,
    additionalTextFontFamily,
  } = values;
  const isAdditionalTextPresent =
    !!additionalText && additionalText.trim() !== '';
  const theme = useTheme();
  const scaledValueForFontSize = 0.31935;
  const scaledHtml = scaleFontSizesInHtml(
    additionalText,
    scaledValueForFontSize
  );

  const cleanedTextValue = scaledHtml.replace(/^(&nbsp;|\s)+/, '');

  return (
    <BodyContainer
      values={values}
      isMobile={isMobile}
      bodyBackgroundColor={bodyBackgroundColor}
      bodyBackgroundImage={bodyBackgroundImage}
      showSalesPreviewContent={showSalesPreviewContent}
    >
      {values.bodyBackgroundImage.url && (
        <StyledBackgroundImage
          src={values.bodyBackgroundImage.url}
          alt='Background'
          id='lp-preview-background-image'
        />
      )}
      {!!showGradient && <GradientOverlay gradient={gradient} />}
      {/* left side */}
      <Flex maxWidth='259px' gap='7px'>
        {showSalesPreviewContent && (
          <Flex
            flexDirection='row'
            justifyContent='center'
            alignItems='center'
            gap='6px'
          >
            {(isMobile
              ? VIDEO_PLAYER_THUMBNAIL_LIST.slice(0, 6)
              : VIDEO_PLAYER_THUMBNAIL_LIST
            ).map(thumbnail => (
              <VideoPlayerThumbnail src={thumbnail} isMobile={isMobile} />
            ))}
          </Flex>
        )}
        <Flex gap={isMobile ? '12px' : '14px'}>
          <VideoPlayerImage src={videoPlayerImage} isMobile={isMobile} />
          {showSalesPreviewContent && (
            <ReactionsAndCommentsContainer isMobile={isMobile}>
              <Gap
                flexDirection='row'
                alignItems='center'
                gap='6px'
                justifyContent='center'
              >
                {REACTIONS.map((reaction: JSX.Element) => reaction)}
                <Button
                  className='font-roboto'
                  text='Comment'
                  variant='white'
                  size='small'
                  style={{
                    minHeight: 16,
                    maxHeight: 16,
                    width: 51,
                    border: `0.5px solid ${theme.colors.secondary[5]}`,
                    fontSize: 6.5,
                    padding: '2px 6px 2px 3px',
                    borderRadius: 3,
                  }}
                  icon={<MdChat width={14} height={14} />}
                />
                <Button
                  className='font-roboto'
                  text='Video Reply'
                  size='small'
                  style={{
                    minHeight: 16,
                    maxHeight: 16,
                    width: 64,
                    fontSize: 6.5,
                    padding: '2px 6px 2px 3px',
                    borderRadius: 3,
                  }}
                  icon={<IoMdVideocam width={14} height={14} />}
                />
              </Gap>
            </ReactionsAndCommentsContainer>
          )}
          <CTAButtonsContainer isMobile={isMobile}>
            {Array.from({ length: ctaButtonCount }).map((_, index) => (
              <CtaButton
                key={index}
                isMobile={isMobile}
                buttonFontCase={buttonFontCase}
                buttonFontWeight={buttonFontWeight}
                buttonFontFamily={buttonFontFamily}
                buttonFontSize={buttonFontSize}
                buttonCornerRadius={buttonCornerRadius}
                buttonPaddingY={buttonPaddingY}
                buttonPaddingX={buttonPaddingX}
                buttonColor={buttonColor}
                buttonHoverColor={buttonHoverColor}
                buttonBorderColor={buttonBorderColor}
                buttonBorderHoverColor={buttonBorderHoverColor}
                showButtonBorder={showButtonBorder}
                buttonTextColor={buttonTextColor}
                buttonTextHoverColor={buttonTextHoverColor}
              >
                Button
              </CtaButton>
            ))}
          </CTAButtonsContainer>
          {isAdditionalTextPresent && (
            <AdditionalTextContainer
              isMobile={isMobile}
              additionalTextBackgroundColor={additionalTextBackgroundColor}
            >
              <AdditionalText
                fontFamily={fontFamily}
                additionalTextColor={additionalTextColor}
                additionalTextFontFamily={additionalTextFontFamily}
              >
                {parse(cleanedTextValue)}
              </AdditionalText>
            </AdditionalTextContainer>
          )}
        </Flex>
      </Flex>
      {/* left side */}
      {/* right side */}
      {showSalesPreviewContent && (
        <Flex maxWidth={isMobile ? '100%' : '167px'} gap='8px'>
          <VehicleInformation isMobile={isMobile} />
          {!isMobile && <MapInformation isMobile={isMobile} />}
        </Flex>
      )}
      {/* right side */}
    </BodyContainer>
  );
};
